import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import {
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteDiagnosticsService,
} from '@store';

import { DiagnosticsUrlTypeEnum } from '../../+installations/installation/installation-overlay.model';

import { RoutingService } from './routing.service';

@Injectable({ providedIn: 'root' })
export class CascadeRemoteDiagnosticsGuard {
  constructor(
    private installationDetailsStore: InstallationDetailsStore,
    private routingService: RoutingService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private remoteDiagnosticsService: RemoteDiagnosticsService
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return combineLatest([
      this.installationDetailsStore.gatewaySerial$,
      this.installationFeaturesStore.featuresLoading$.pipe(filter((isLoading) => !isLoading)),
      this.installationFeaturesStore.isFeaturesRequestFinished$.pipe(
        filter((isFinished) => isFinished)
      ),
    ]).pipe(
      take(1),
      switchMap(([gatewaySerial]) =>
        combineLatest([
          this.installationDetailsStore.isHydraulicSchemeMenuItemVisible(gatewaySerial),
          this.installationDetailsStore.isSelectedGatewayAV2Cascade$.pipe(
            filter((value) => value !== undefined)
          ),
          this.installationDetailsStore.isExtendedParametersMenuItemVisible(gatewaySerial),
          this.remoteDiagnosticsService.getRemoteDiagnosticsAccessAndConnectionStatus(),
        ])
      ),

      map(
        ([
          isHydraulicSchemeMenuItemVisible,
          isSelectedGatewayAV2Cascade,
          isExtendedParametersMenuItemVisible,
          remoteDiagnosticsAccessAndConnectionStatus,
        ]) => {
          if (isHydraulicSchemeMenuItemVisible && !isSelectedGatewayAV2Cascade) {
            return true;
          }

          if (
            remoteDiagnosticsAccessAndConnectionStatus.isDiagnosticsSessionRunning &&
            !remoteDiagnosticsAccessAndConnectionStatus.hasAccessToDiagnostics
          ) {
            return true;
          }

          if (
            !isHydraulicSchemeMenuItemVisible &&
            isSelectedGatewayAV2Cascade &&
            isExtendedParametersMenuItemVisible
          ) {
            // cancel navigation
            this.routingService.changeChildInGuard(
              state.url,
              DiagnosticsUrlTypeEnum.ExtendedParameter
            );
          }

          this.routingService.navigateToInstallationDashboardFromGuard(state);

          return false;
        }
      )
    );
  }
}
